import { alpha } from "@mui/material/styles";

// ----------------------------------------------------------------------

// SETUP COLORS
const GREY = {
    0: "#FFFFFF",
    100: "#F9FAFB",
    200: "#F4F6F8",
    300: "#9D9D9D",
    400: "#C4CDD5",
    500: "#919EAB",
    600: "#4D4D4D",
    700: "#454F5B",
    800: "#292929",
    900: "#1C1C1E"
};

const PRIMARY = {
    lighter: "#C8FAD6",
    light: "#5BE49B",
    main: "#2C6CD2",
    dark: "#2C6CD2",
    darker: "#004B50",
    contrastText: "#FFFFFF"
};

const SECONDARY = {
    lighter: "#EEE8F3",
    light: "#E9DDF4",
    main: "#7A00E6",
    dark: "#00AB55",
    darker: "#27097A",
    contrastText: "#FFFFFF"
};

const INFO = {
    lighter: "#E8EDF3", // main 5%
    light: "#DDE7F2", // main 10%
    main: "#0067D7",
    dark: "#0C53B7",
    darker: "#04297A",
    contrastText: "#FFFFFF"
};

const SUCCESS = {
    lighter: "#EBF2EC",
    light: "#EBF9EE",
    main: "#35C559",
    dark: "#0D8348",
    darker: "#0B5932",
    contrastText: "#ffffff"
};

const WARNING = {
    lighter: "#F5F2E8",
    light: "#F6F1DD",
    main: "#FECC00",
    dark: "#B78103",
    darker: "#7A4F01",
    contrastText: GREY[ 800 ]
};

const ERROR = {
    lighter: "#F4EBEA",
    light: "#F6E2E1",
    main: "#F8362B",
    dark: "#B72136",
    darker: "#7A0C2E",
    contrastText: "#FFFFFF"
};

const BASIC = {
    lighter: "#909090",
    light: "#909090",
    main: "#1C1C1E",
    dark: "#909090",
    darker: "#909090",
    contrastText: "#909090"
};

const CHART_COLORS = {
    violet: [ "#826AF9", "#9E86FF", "#D0AEFF", "#F7D2FF" ],
    blue: [ "#2D99FF", "#83CFFF", "#A5F3FF", "#CCFAFF" ],
    green: [ "#2CD9C5", "#60F1C8", "#A4F7CC", "#C0F2DC" ],
    yellow: [ "#FFE700", "#FFEF5A", "#FFF7AE", "#FFF3D6" ],
    red: [ "#FF6C40", "#FF8F6D", "#FFBD98", "#FFF2D4" ]
};

// ----------------------------------

const DEFINED = {
    text1: GREY[ 900 ],
    text2: GREY[ 600 ],
    text3: GREY[ 300 ],
    line: "#E3E3E3",
    gray: "#F0F0F0",
    gray_black: GREY[ 900 ],
    gray_white: "#F6F6F6",
    white: "#FFFFFF",
    black: "#000000",
    green: SUCCESS.main,
    blue: INFO.main,
    yellow: WARNING.main,
    red: ERROR.main,
    purple: PRIMARY.main
};

const COMMON = {
    common: {
        black: "#000000",
        white: "#FFFFFF",
        text1: "#292929",
        text2: "#909090",
        line: "#D8DDE1",
        gray: "#F6F6F6",
        grayWhite: "#FBFBFB"
    },
    primary: PRIMARY,
    secondary: SECONDARY,
    info: INFO,
    success: SUCCESS,
    warning: WARNING,
    error: ERROR,
    basic: BASIC,
    grey: GREY,
    chart: CHART_COLORS,
    divider: alpha(GREY[ 500 ], 0.2),
    action: {
        hover: alpha(GREY[ 500 ], 0.08),
        selected: alpha(GREY[ 500 ], 0.16),
        disabled: alpha(GREY[ 500 ], 0.8),
        // disabledBackground: alpha(GREY[500], 0.24),
        disabledBackground: GREY[ 600 ],
        focus: alpha(GREY[ 500 ], 0.24),
        hoverOpacity: 0.08,
        disabledOpacity: 0.48
    }
};

export function palette(mode) {
    const light = {
        ...COMMON,
        mode: "light",
        text: {
            primary: DEFINED.text1,
            secondary: DEFINED.text2,
            tertiary: DEFINED.text3,
            altered: DEFINED.white,
            disabled: DEFINED.text3,
            blue: DEFINED.blue,
            line: DEFINED.line
        },
        button: {
            text_light: DEFINED.text2,
            blue_light: "#318EF4",
            red_light: "#F56158",
            green_light: "#57E17A",
            purple_light: "#9329F0",
            gray_light: "#FBFBFB"
        },
        background: {
            default: DEFINED.white,
            gray: DEFINED.gray,
            gray_dark: DEFINED.gray_black,
            gray_light: DEFINED.gray_white,
            success: "#CFEBD6",
            fail: "#F6CFCD"
        },
        line: "#E3E3E3",
        action: {
            ...COMMON.action,
            active: GREY[ 600 ]
        }
    };

    const dark = {
        ...COMMON,
        mode: "dark",
        text: {
            primary: "#FFFFFF",
            secondary: GREY[ 500 ],
            tertiary: GREY[ 500 ],
            alter: GREY[ 900 ],
            disabled: GREY[ 600 ]
        },
        button: {
            text_light: DEFINED.text2,
            blue_light: "#318EF4",
            red_light: "#F56158",
            green_light: "#57E17A",
            purple_light: "#9329F0",
            gray_light: "#FBFBFB"
        },
        background: {
            paper: GREY[ 900 ],
            default: GREY[ 900 ],
            neutral: alpha(GREY[ 500 ], 0.12),
            gray: "#F6F6F6",
            gray_dark: GREY[ 500 ],
            gray_light: "#FBFBFB"
        },
        line: "#D8DDE1",
        action: {
            ...COMMON.action,
            active: GREY[ 500 ]
        }
    };

    return mode === "light" ? light : dark;
}
